import React, { useState, useRef, useEffect } from "react";
import { Button } from "../../components/ui";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🎉"];

/**
 * Reaction chips + emoji picker under a message bubble.
 */
const MessageReactions = ({
  reactions = [],
  currentUserId,
  onToggleReaction,
  own = false,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickerOpen]);

  const normalizeId = (v) => {
    if (v == null) return "";
    if (typeof v === "object") return (v._id ?? v.id ?? "").toString();
    return String(v);
  };

  const me = normalizeId(currentUserId);

  // Group by emoji → { emoji, count, reactedByMe }
  const grouped = {};
  (reactions || []).forEach((r) => {
    const key = r.emoji;
    if (!grouped[key]) {
      grouped[key] = { emoji: key, count: 0, reactedByMe: false };
    }
    grouped[key].count += 1;
    if (me && normalizeId(r.userId) === me) {
      grouped[key].reactedByMe = true;
    }
  });

  const chips = Object.values(grouped);

  const handlePick = (emoji) => {
    onToggleReaction?.(emoji);
    setPickerOpen(false);
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-1 mt-1.5 relative ${
        own ? "justify-end" : "justify-start"
      }`}
    >
      {chips.map((c) => (
        <button
          key={c.emoji}
          type="button"
          onClick={() => handlePick(c.emoji)}
          title={c.reactedByMe ? "Remove reaction" : "React"}
          className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border transition-colors ${ c.reactedByMe ? "bg-neutral-900 border-neutral-900 text-white" : "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-800 text-neutral-700 dark:text-neutral-200 hover:border-neutral-300" }`}
        >
          <span>{c.emoji}</span>
          <span className="font-semibold">{c.count}</span>
        </button>
      ))}

      <div className="relative" ref={pickerRef}>
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          className="opacity-0 group-hover:opacity-100 text-[11px] px-2 py-0.5 rounded-full border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-all shadow-xs"
          title="Add reaction"
        >
          😊 +
        </button>

        {pickerOpen && (
          <div
            className={`absolute z-20 bottom-full mb-1 flex gap-1.5 p-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl shadow-xl ${ own ? "right-0" : "left-0" }`}
          >
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => handlePick(emoji)}
                className="text-lg leading-none p-1.5 rounded-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageReactions;